import { useHistory, useLocation } from "react-router-dom";
import { Card, Flex, FullscreenTakeover, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column } from "@jsq/ds/layouts";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import {
  dividerStyle,
  optionRowStyle,
} from "~/pages/Compliance/profile/ProfileCreator/ProfileCategorySelector/styles_vanilla.css";
import { BackButtonWithinForm } from "../BackButtonWithinForm";
import { isEntityClientOnboardingCase } from "../utils/isEntityClientOnboardingCase";

interface Props {
  caseId: number;
}

type AccountOption = {
  label: string;
  pathSegment: "entity" | "individual";
};

const INVESTOR_ACCOUNT_OPTIONS: AccountOption[] = [
  { label: "Individual", pathSegment: "individual" },
  { label: "Entity", pathSegment: "entity" },
];

const ONBOARDING_ACCOUNT_OPTIONS: AccountOption[] = [
  { label: "Entity", pathSegment: "entity" },
  { label: "Individual", pathSegment: "individual" },
];

const SelectOwnerOrControllerForm = ({ caseId }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { caseType } = useCurrentCaseContext();
  const isOnboardingCase = isEntityClientOnboardingCase(caseType);
  const accountOptions = isOnboardingCase
    ? ONBOARDING_ACCOUNT_OPTIONS
    : INVESTOR_ACCOUNT_OPTIONS;

  const onExit = () => {
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles`);
  };

  const onAccountTypeClick = (pathSegment: "entity" | "individual") => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    const suffix = isOnboardingCase ? `/${pathSegment}/method` : `/${pathSegment}`;
    const url = `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers${suffix}${queryString ? `?${queryString}` : ""}`;
    history.push(url);
  };

  return (
    <FullscreenTakeover.Body>
      <Column size="lg">
        <Card data-testid="fundraisingAndOwnershipCard">
          <BackButtonWithinForm onClick={onExit} displayText="All owners and controllers" />
          <Text marginBottom="sm" variant="h3">
            Add owner or controller
          </Text>
          <Text marginBottom="sm" variant="regular-md">
            Select an account type
          </Text>
          {accountOptions.map((option, index) => (
            <div key={option.pathSegment}>
              <button
                className={optionRowStyle}
                type="button"
                onClick={() => onAccountTypeClick(option.pathSegment)}
                tabIndex={0}
              >
                <Flex alignItems="center" justifyContent="space-between">
                  <Text asSpan data-testid="optionTitle" variant="strong-lg">
                    {option.label}
                  </Text>
                  <Icon
                    type="chevron-right"
                    color={Colors.Neutral600}
                    variant="outline"
                    size="large"
                  />
                </Flex>
              </button>
              {index < accountOptions.length - 1 && <div className={dividerStyle} />}
            </div>
          ))}
        </Card>
      </Column>
    </FullscreenTakeover.Body>
  );
};

export default SelectOwnerOrControllerForm;
